import React from 'react';
import { ContactInfoFieldKeyType } from 'typings/form';
import styles from 'components/common/contactPerson/styles.module.scss';
import { Button, Col, ContactFieldRow, Divider, IconWrapper, Row, Typography } from 'components/index';
import { DeleteIcon, EditIcon, OutlineArrowDownIcon, OutlineArrowUpIcon } from 'components/common/icons';
import { useContactPersonCard } from 'components/common/contactPerson/contactPersonCard/useContactPersonCard';

const { Text } = Typography;

type Language = {
  lastModifiedDate: string;
  createDate: string;
  id: string;
  name: string;
  flagLocation: string;
  [key: string]: any;
};

export type ContactPersonData = {
  contacts: Record<ContactInfoFieldKeyType, string[]>;
  department: string;
  fullName: string;
  id: string;
  language: Language;
  languageId: string;
  role: string;
};

type ContactPersonCard = {
  data: ContactPersonData;
  hasAccessToWrite: boolean;
  hasAccessToDelete: boolean;
  onDeleteModalOpen: (id: string) => void;
  onUpdateModalOpen: (id: string) => void;
};

export const ContactPersonCard = React.memo((props: ContactPersonCard) => {
  const { data, hasAccessToWrite, hasAccessToDelete, onUpdateModalOpen, onDeleteModalOpen } = props;
  const { fullName, language, id, department, role, contacts } = data;
  const { onShowAllStateChange, isAllShown, isSeeAllVisible, personContactInfo } = useContactPersonCard(contacts);
  return (
    <Col span={24}>
      <Col span={24} className={'margin-b-20'}>
        <Row gutter={[0, 4]}>
          <Col span={24}>
            <Row justify={'space-between'} align={'middle'}>
              <Col>
                <Row className={styles.name_flag_container} align={'middle'}>
                  <Text type={'secondary'}>{fullName}</Text>
                  {language?.flagLocation && <IconWrapper src={language?.flagLocation} />}
                </Row>
              </Col>
              <Col>
                <Row className={styles.button_container} justify={'end'}>
                  {hasAccessToDelete && (
                    <Button type={'link'} icon={<DeleteIcon />} onClick={() => onDeleteModalOpen(id)} />
                  )}
                  {hasAccessToWrite && (
                    <Button type={'link'} icon={<EditIcon />} onClick={() => onUpdateModalOpen(id)} />
                  )}
                </Row>
              </Col>
            </Row>
          </Col>
          {department && (
            <Col span={24}>
              <Row justify={'space-between'}>
                <Col>
                  <Text type={'secondary'}>Department</Text>
                </Col>
                <Col>
                  <Text type={'secondary'}>{department}</Text>
                </Col>
              </Row>
            </Col>
          )}
          {role && (
            <Col span={24}>
              <Row justify={'space-between'}>
                <Col>
                  <Text type={'secondary'}>Role</Text>
                </Col>
                <Col>
                  <Text type={'secondary'}>{role}</Text>
                </Col>
              </Row>
            </Col>
          )}
          {Object.entries(personContactInfo).map(([type, values]) => (
            <ContactFieldRow fieldValueMaxWidth={300} key={type} type={type as ContactInfoFieldKeyType} data={values} />
          ))}

          {isSeeAllVisible && (
            <Col span={24}>
              <Row align={'middle'} justify={'end'}>
                <Col>
                  <Button
                    type={'link'}
                    variant={'link'}
                    style={{
                      color: '#308D82',
                      textDecoration: 'underline',
                      padding: 0,
                      margin: 0,
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                    onClick={onShowAllStateChange}
                  >
                    {isAllShown ? 'See Less' : 'See All'}
                    {isAllShown ? (
                      <OutlineArrowUpIcon size={'sm'} color={'#308D82'} />
                    ) : (
                      <OutlineArrowDownIcon size={'sm'} color={'#308D82'} />
                    )}
                  </Button>
                </Col>
              </Row>
            </Col>
          )}
        </Row>
      </Col>
      <Col span={24}>
        <Divider className={styles.divider} />
      </Col>
    </Col>
  );
});
